import {clearAllBodyScrollLocks, disableBodyScroll} from "body-scroll-lock";
import clsx from "clsx";
import * as React from "react";
import {User} from "@/contexts/common/AuthContextProvider";
import useScreenSize from "@/hooks/useScreenSize";
import {GlobalNavSectionType, SIDENAV_FADE_DURATION} from "@/types/common/library";
import NavLayoutHeader from "./NavLayoutHeader";
import NavLayoutSidebar from "./NavLayoutSidebar";
export type NayLayoutProps = {
   className?: string;
   sideBarClassName?: string;
   mainClassName?: string;
   menuSections?: GlobalNavSectionType[];
   user?: User;
   userActions: {
      onLogoutClick: () => void;
      onUserManagementClick?: () => void;
   };
   children?: React.ReactNode;
   logo: React.ReactNode;
   minimizedLogo: React.ReactNode;
   sideNavMessage?: string;
   userHasAlert: boolean;
   isSuperAdmin?: boolean;
   /** Visually recede the sidebar (e.g. while the assistant dock is open). */
   isNavDimmed?: boolean;
};

const NavLayout: React.FC<NayLayoutProps> = ({
   className,
   sideBarClassName,
   mainClassName,
   menuSections = [],
   user,
   children,
   userActions,
   logo,
   minimizedLogo,
   sideNavMessage,
   userHasAlert,
   isSuperAdmin,
   isNavDimmed,
}) => {
   const {isMobile} = useScreenSize();
   const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
   const [isSidebarMinimized, setIsSidebarMinimized] = React.useState(false);

   const sidebarRef = React.useRef<HTMLElement>(null);

   React.useEffect(() => {
      if (isMobile) {
         if (isMobileMenuOpen) {
            if (
               sidebarRef.current?.id &&
               document &&
               document.querySelector(`#${sidebarRef.current.id}`)
            ) {
               disableBodyScroll(
                  document.querySelector(`#${sidebarRef.current.id}`) as HTMLElement
               );
            }
         } else {
            clearAllBodyScrollLocks();
         }
      }

      return () => {
         clearAllBodyScrollLocks();
      };
   }, [isMobileMenuOpen, isMobile]);

   const handleToggleMenu = () => {
      setIsMobileMenuOpen((prev) => !prev);
   };

   const handleCloseMenu = () => {
      setIsMobileMenuOpen(false);
   };

   const handleMinimizeChange = (isMinimized: boolean) => {
      setIsSidebarMinimized(isMinimized);
   };

   return (
      <div
         data-testid="nayLayout_root"
         style={
            {
               "--fade-duration": `${SIDENAV_FADE_DURATION}ms`,
               transition: "grid-template-columns var(--fade-duration)",
               gridTemplateColumns: isMobile
                  ? "minmax(0,1fr)"
                  : `${isSidebarMinimized ? "64px" : "14rem"} minmax(0,1fr)`,
            } as React.CSSProperties
         }
         className={clsx(
            "grid",
            "min-h-screen",
            "relative",
            // `clip` instead of `hidden`: same clipping, but it doesn't create
            // a scroll container, so sticky children (nav sidebar, assistant
            // dock) can pin to the viewport while the page body-scrolls.
            "overflow-clip",
            "pt-16",
            "md:pt-0",
            className
         )}
      >
         <NavLayoutSidebar
            {...{
               menuSections,
               user,
               isMobileMenuOpen,
               sideBarRef: sidebarRef,
               isMobile,
               userActions,
               className: sideBarClassName,
               logo,
               minimizedLogo,
               sideNavMessage,
               userHasAlert,
               isSuperAdmin,
               onItemClick: isMobile ? handleCloseMenu : undefined,
               onMinimizeChange: handleMinimizeChange,
               isDimmed: isNavDimmed,
            }}
         />
         <main data-testid="nayLayout_main" className={clsx(mainClassName)}>
            {isMobile && (
               <NavLayoutHeader
                  onToggleMenu={handleToggleMenu}
                  logo={logo}
                  isOpen={isMobileMenuOpen}
               />
            )}

            {children}
         </main>
      </div>
   );
};

export default NavLayout;
